import { useEffect, useState } from "react";
import { Cookie } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { setHeyCatchConsent } from "@/lib/heycatch";

const NAME = "cr_consent";
const MAX_AGE = 60 * 60 * 24 * 182;
const OPEN = "cr-open-cookie-settings";

// Stored as "a1r0" (analytics / session recording); legacy "all" and "necessary" still read correctly.
const read = () => {
  const v = (document.cookie.match(/(?:^|; )cr_consent=([^;]+)/) || [])[1];
  if (!v) return null;
  if (v === "all") return { analytics: true, recording: true };
  return { analytics: v.includes("a1"), recording: v.includes("r1") };
};

const store = ({ analytics, recording }) => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${NAME}=a${analytics ? 1 : 0}r${recording ? 1 : 0}; Max-Age=${MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN));

export const CookieSettingsLink = ({ testid = "cookie-settings-link", className = "hover:text-white underline" }) => (
  <button type="button" data-testid={testid} onClick={openCookieSettings} className={className}>Cookie settings</button>
);

const Toggle = ({ id, title, desc, checked, onChange }) => (
  <label htmlFor={id} className="flex items-start justify-between gap-4 py-3">
    <span>
      <span className="block text-sm text-white">{title}</span>
      <span className="block text-xs text-[#A3A3A3] mt-0.5">{desc}</span>
    </span>
    <Switch id={id} data-testid={id} checked={checked} onCheckedChange={onChange} className="data-[state=checked]:bg-[#00FF9D]" />
  </label>
);

const Choices = ({ initial, onSave }) => {
  const [c, setC] = useState(initial);
  return (
    <div data-testid="cookie-choices" className="mt-4 divide-y divide-[#2A2A2A] border-y border-[#2A2A2A]">
      <div className="flex items-center justify-between py-3">
        <span><span className="block text-sm text-white">Necessary</span><span className="block text-xs text-[#A3A3A3] mt-0.5">Sign-in and security. Always on.</span></span>
        <span className="font-mono text-[10px] text-[#00FF9D]">ALWAYS ON</span>
      </div>
      <Toggle id="cookie-analytics-switch" title="Analytics" desc="Page views and clicks, so we can see which features help."
        checked={c.analytics} onChange={(v) => setC({ ...c, analytics: v })} />
      <Toggle id="cookie-recording-switch" title="Session recording" desc="Replays of how pages are used, with typed text hidden."
        checked={c.recording} onChange={(v) => setC({ ...c, recording: v })} />
      <div className="py-3">
        <button data-testid="cookie-save-choices-btn" onClick={() => onSave(c)}
          className="w-full rounded-full bg-white py-2.5 text-sm font-semibold text-[#050505] hover:bg-[#00FF9D] transition-colors">Save choices</button>
      </div>
    </div>
  );
};

export const CookieConsent = () => {
  const [open, setOpen] = useState(() => !read());
  const [choosing, setChoosing] = useState(false);
  useEffect(() => {
    const show = () => { setOpen(true); setChoosing(true); };
    window.addEventListener(OPEN, show);
    return () => window.removeEventListener(OPEN, show);
  }, []);
  const choose = (next) => {
    const was = read() || { analytics: false, recording: false };
    store(next);
    setOpen(false);
    setChoosing(false);
    if (!window.__crAnalyticsOn) {
      if (next.analytics || next.recording) window.__crInitAnalytics?.(next);
      setHeyCatchConsent(!!next.analytics);
    } else if (was.analytics !== next.analytics || was.recording !== next.recording) {
      if (!next.analytics && !next.recording) window.posthog?.opt_out_capturing?.();
      setHeyCatchConsent(!!next.analytics);
      window.location.reload();
    }
  };
  if (!open) return null;
  return (
    <div role="dialog" aria-live="polite" aria-label="Cookie choices" data-testid="cookie-consent"
      className="fixed bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-auto z-[80] sm:max-w-md max-h-[85vh] overflow-y-auto rounded-2xl border border-[#2A2A2A] bg-[#0A0A0A]/95 backdrop-blur-xl p-4 sm:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)] animate-in fade-in-0 slide-in-from-bottom-4">
      <div className="flex items-start gap-3">
        <span className="hidden sm:grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[#2A2A2A] bg-[#121212]"><Cookie size={18} className="text-[#00FF9D]" /></span>
        <div className="text-sm">
          <p className="font-display text-base text-white">Cookies on CalibRunway</p>
          <p className="mt-1 text-xs sm:text-sm text-[#A3A3A3] leading-relaxed">
            We use necessary cookies to sign you in and keep your account secure. With your OK, we also use analytics and session recording to improve the site.
            {" "}<a href="/terms#privacy" data-testid="cookie-privacy-link" className="text-white underline hover:text-[#00FF9D]">Privacy policy</a>
          </p>
        </div>
      </div>
      {choosing && <Choices initial={read() || { analytics: false, recording: false }} onSave={choose} />}
      <div className="mt-4 flex gap-2">
        <button data-testid="cookie-necessary-btn" onClick={() => choose({ analytics: false, recording: false })}
          className="flex-1 rounded-full border border-[#2A2A2A] py-2.5 text-xs sm:text-sm hover:border-white transition-colors">Necessary only</button>
        {!choosing && <button data-testid="cookie-choose-btn" onClick={() => setChoosing(true)}
          className="flex-1 rounded-full border border-[#2A2A2A] py-2.5 text-xs sm:text-sm hover:border-white transition-colors">Choose</button>}
        <button data-testid="cookie-accept-all-btn" onClick={() => choose({ analytics: true, recording: true })}
          className="flex-1 rounded-full bg-[#00FF9D] py-2.5 text-xs sm:text-sm font-semibold text-[#050505] hover:shadow-[0_0_20px_rgba(0,255,157,0.5)] transition-shadow">Accept all</button>
      </div>
    </div>
  );
};
